import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, getPublicSettings } from "../../services/adminApi";
import logo from "../../assets/logo.png";

export default function AdminLogin({ onLogin }) {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getPublicSettings().then((d) => setCompanyName(d.companyName || d.companyTradeName || ""));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { admin } = await adminApi.login(email, password);
      onLogin(admin);
    } catch (err) {
      setError(err.message || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex" style={{ background: "linear-gradient(160deg, #0d1b3e 0%, #0a0f2c 50%, #060b1f 100%)" }}>
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between flex-1 p-16" style={{ position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 50% at 25% 20%, rgba(59,130,246,0.18) 0%, transparent 70%), radial-gradient(ellipse 40% 40% at 85% 80%, rgba(99,102,241,0.13) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div className="relative z-10">
          <img src={logo} alt="TaxiOps" className="h-14 w-14 object-contain rounded-2xl" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }} />
        </div>
        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white leading-tight mb-4" style={{ letterSpacing: "-0.025em" }}>
            Keep your fleet<br />compliant, always.
          </h1>
          <p className="text-blue-200/70 text-lg leading-relaxed max-w-sm">
            Track driver documents, manage applications, and stay ahead of every renewal deadline.
          </p>
        </div>
        <p className="relative z-10 text-slate-500/60 text-xs">
          © {new Date().getFullYear()} Old Alex Hub, LLC · All rights reserved
        </p>
      </div>

      {/* Right: sign-in card */}
      <div className="flex items-center justify-center w-full lg:w-[460px] lg:flex-shrink-0 px-6 py-12 lg:bg-[#050816]">
        <div className="w-full max-w-sm">
          {/* Mobile: back + logo */}
          <div className="lg:hidden mb-8">
            <button
              onClick={() => navigate("/enrollme")}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition mb-6"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div className="text-center">
              <img src={logo} alt="EnrollMe" className="h-12 w-12 object-contain rounded-xl mx-auto mb-4" />
              <p className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-0.5">Compliance Tower</p>
              <p className="text-lg font-bold text-white">{companyName}</p>
            </div>
          </div>

          <button
            onClick={() => navigate("/enrollme")}
            className="hidden lg:flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition mb-8"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="mb-8">
            <p className="hidden lg:block text-xs font-semibold text-blue-400 uppercase tracking-widest mb-2">Compliance Tower</p>
            <h2 className="hidden lg:block text-3xl font-bold text-white mb-1" style={{ letterSpacing: "-0.02em" }}>Welcome back</h2>
            <p className="hidden lg:block text-slate-400 text-sm">Sign in to the admin dashboard.</p>
          </div>

          {error && (
            <div className="rounded-lg px-4 py-3 mb-6 text-sm" style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", color: "#fca5a5" }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Work email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="admin@company.com"
                className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none transition"
                style={{ background: "rgba(15,23,42,0.65)", border: "1px solid rgba(148,163,184,0.18)", boxSizing: "border-box" }}
                onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.boxShadow = "0 0 0 3px rgba(59,130,246,0.15)"; }}
                onBlur={(e) => { e.target.style.borderColor = "rgba(148,163,184,0.18)"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none transition"
                style={{ background: "rgba(15,23,42,0.65)", border: "1px solid rgba(148,163,184,0.18)", boxSizing: "border-box" }}
                onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.boxShadow = "0 0 0 3px rgba(59,130,246,0.15)"; }}
                onBlur={(e) => { e.target.style.borderColor = "rgba(148,163,184,0.18)"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full font-semibold py-3 rounded-xl transition disabled:opacity-50 text-sm"
              style={{ background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", marginTop: "4px" }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
